import {
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect } from 'react';
import './Form.styles.scss';
import { useFormik } from 'formik';
import schema from '../../../schemas/contactUsSchema';
import Icon from '../../../components/Icon/Icon';
import Button from '../../../components/Button/Button';
import { useNavigate, useSearchParams } from 'react-router-dom';
import usePipedrive from '../../../hooks/usePipedrive';

const Form: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [errorText, setErrorText] = React.useState<string | null>(null);
  const { createPerson, createOrganization, createLead } = usePipedrive();

  const inquiryReason: Record<string, string> = {
    'teaming-opportunities': 'Teaming Opportunities',
    'partnership-inquiries': 'Partnership Inquiries',
    'client-support': 'Client Support Requests',
    'service-inquiries': 'Service Inquiries',
    'research-collaborations': 'Research Collaborations',
    'media-inquiries': 'Media Inquiries',
    'investor-inquiries': 'Investor Inquiries',
    'edtech-programs': 'EdTech Programs',
    'data-marketplace': 'Data Marketplace',
  };

  const inquiryOptions = Object.values(inquiryReason);

  const getInitialReason = () => {
    const reasonParam = searchParams.get('reason');
    if (reasonParam && reasonParam in inquiryReason) {
      return inquiryReason[reasonParam];
    }
    return '';
  };

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      inquiryReason: getInitialReason(),
      organization: '',
      organizationWebsite: '',
      organizationName: '',
      pointOfContactRole: '',
      desiredCollaborationScope: '',
      clientProjectName: '',
      serviceDetails: '',
      typeOfCollaboration: '',
      relevantBackground: '',
      mediaOutlet: '',
      deadline: '',
      investmentInterestType: '',
      programInterestType: '',
      desiredProgramDates: '',
      numberOfParticipants: '',
      additionalProgramDetails: '',
      marketplaceType: '',
      dataUseCase: '',
      specificDataset: '',
      preferredDeliveryFormat: '',
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      const baseUrl = window.location.origin;
      const environment = baseUrl === '**insert prod url here**' ? '' : 'DEV';
      try {
        const person = await createPerson({
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phoneNumber: values.phoneNumber,
        });

        const organization = await createOrganization({
          name: values.organization,
          address: values.organizationWebsite,
        });

        const leadData = {
          title: `GGenomics Website ${environment}`,
          organization_id: organization.id,
          person_id: person.id,
          inquiry_reason: values.inquiryReason,
          point_of_contact: values.pointOfContactRole,
          desired_collaboration_scope: values.desiredCollaborationScope,
          client_project_name: values.clientProjectName,
          detail_of_service: values.serviceDetails,
          type_of_collaboration: values.typeOfCollaboration,
          relevant_background: values.relevantBackground,
          media_deadline: values.deadline,
          investor_interest_type: values.investmentInterestType,
          program_interest_type: values.programInterestType,
          desired_program_dates: values.desiredProgramDates,
          number_of_participants: values.numberOfParticipants.toString(),
          additional_program_details: values.additionalProgramDetails,
          marketplace_type: values.marketplaceType,
          data_use_case: values.dataUseCase,
          specific_dataset: values.specificDataset,
          preferred_delivery_format: values.preferredDeliveryFormat,
        };

        await createLead(leadData);
        navigate('/contact-us/success');
      } catch {
        setErrorText('An error occurred. Please try again later.');
      }
    },
  });

  useEffect(() => {
    const reason = getInitialReason();
    if (reason) {
      formik.setFieldValue('inquiryReason', reason);
    }
    // Only re-sync the inquiry field when the URL query params change.
    // `getInitialReason` and `formik` are recreated every render, so including
    // them (as exhaustive-deps wants) would re-run this on every render and
    // clobber user input. Intentionally depending on `searchParams` only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <Box className="form-wrapper">
      <form onSubmit={formik.handleSubmit}>
        <Box className="first-last-name-row">
          <TextField
            label="First Name*"
            placeholder="First Name"
            name="firstName"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            fullWidth
            variant="outlined"
            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
            helperText={formik.touched.firstName && formik.errors.firstName}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon icon="person" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <TextField
            label="Last Name*"
            name="lastName"
            placeholder="Last Name"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            fullWidth
            variant="outlined"
            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
            helperText={formik.touched.lastName && formik.errors.lastName}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon icon="person" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>

        <TextField
          label="Email*"
          name="email"
          placeholder="Email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          fullWidth
          variant="outlined"
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon="mail" />
                </InputAdornment>
              ),
            },
          }}
        />

        <TextField
          label="Organization*"
          name="organization"
          value={formik.values.organization}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          fullWidth
          variant="outlined"
        />

        <Box className="first-last-name-row">
          <TextField
            label="Phone Number"
            name="phoneNumber"
            value={formik.values.phoneNumber}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            fullWidth
            variant="outlined"
          />

          <TextField
            label="Organization Website*"
            name="organizationWebsite"
            value={formik.values.organizationWebsite}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            fullWidth
            variant="outlined"
          />
        </Box>

        <FormControl fullWidth>
          <InputLabel>Inquiry Reason*</InputLabel>
          <Select
            name="inquiryReason"
            value={formik.values.inquiryReason}
            onChange={(e) => {
              formik.handleChange(e);

              const selectedValue = e.target.value;

              // Reset conditional fields based on the selected inquiry reason
              if (
                selectedValue !== 'Teaming Opportunities' &&
                selectedValue !== 'Partnership Inquiries'
              ) {
                formik.setFieldValue('pointOfContactRole', '');
                formik.setFieldValue('desiredCollaborationScope', '');
              }
              if (
                selectedValue !== 'Client Support Requests' &&
                selectedValue !== 'Service Inquiries'
              ) {
                formik.setFieldValue('clientProjectName', '');
                formik.setFieldValue('serviceDetails', '');
              }
              if (selectedValue !== 'Research Collaborations') {
                formik.setFieldValue('typeOfCollaboration', '');
                formik.setFieldValue('relevantBackground', '');
              }
              if (selectedValue !== 'Media Inquiries') {
                formik.setFieldValue('deadline', '');
              }
              if (selectedValue !== 'Investor Inquiries') {
                formik.setFieldValue('investmentInterestType', '');
              }
              if (selectedValue !== 'EdTech Programs') {
                formik.setFieldValue('programInterestType', '');
                formik.setFieldValue('desiredProgramDates', '');
                formik.setFieldValue('numberOfParticipants', '');
                formik.setFieldValue('additionalProgramDetails', '');
              }
              if (selectedValue !== 'Data Marketplace') {
                formik.setFieldValue('marketplaceType', '');
                formik.setFieldValue('dataUseCase', '');
                formik.setFieldValue('specificDataset', '');
                formik.setFieldValue('preferredDeliveryFormat', '');
              }

              // Update the URL with the selected inquiry reason
              const urlParam = Object.entries(inquiryReason).find(
                ([_, value]) => value === selectedValue
              )?.[0];
              if (urlParam) {
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.set('reason', urlParam);
                navigate(`?${newSearchParams.toString()}`, { replace: true });
              }
            }}
            onBlur={formik.handleBlur}
            error={
              formik.touched.inquiryReason &&
              Boolean(formik.errors.inquiryReason)
            }
          >
            {inquiryOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Conditional Fields based on Inquiry Reason */}
        {['Teaming Opportunities', 'Partnership Inquiries'].includes(
          formik.values.inquiryReason
        ) && (
          <>
            <TextField
              label="Point of Contact Role/Title*"
              name="pointOfContactRole"
              value={formik.values.pointOfContactRole}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.pointOfContactRole &&
                Boolean(formik.errors.pointOfContactRole)
              }
              helperText={
                formik.touched.pointOfContactRole &&
                formik.errors.pointOfContactRole
              }
              fullWidth
            />
            <TextField
              label="Desired Collaboration Scope"
              name="desiredCollaborationScope"
              value={formik.values.desiredCollaborationScope}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              multiline
              InputProps={{
                style: {
                  minHeight: '120px',
                  alignItems: 'flex-start',
                },
              }}
              inputProps={{
                style: {
                  overflow: 'auto',
                  overflowWrap: 'break-word',
                  whiteSpace: 'pre-wrap',
                },
              }}
            />
          </>
        )}

        {['Client Support Requests', 'Service Inquiries'].includes(
          formik.values.inquiryReason
        ) && (
          <>
            <TextField
              label="Client/Project Name"
              name="clientProjectName"
              value={formik.values.clientProjectName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
            />
            <TextField
              label="Details of Service/Support"
              name="serviceDetails"
              value={formik.values.serviceDetails}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              multiline
              InputProps={{
                style: {
                  minHeight: '120px',
                  alignItems: 'flex-start',
                },
              }}
              inputProps={{
                style: {
                  overflow: 'auto',
                  overflowWrap: 'break-word',
                  whiteSpace: 'pre-wrap',
                },
              }}
            />
          </>
        )}

        {formik.values.inquiryReason === 'Research Collaborations' && (
          <>
            <FormControl fullWidth>
              <InputLabel>Type of Collaboration</InputLabel>
              <Select
                name="typeOfCollaboration"
                value={formik.values.typeOfCollaboration}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <MenuItem value="Academic">Academic</MenuItem>
                <MenuItem value="Corporate">Corporate</MenuItem>
                <MenuItem value="Government">Government</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Relevant Background/Focus Area"
              name="relevantBackground"
              value={formik.values.relevantBackground}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              multiline
              InputProps={{
                style: {
                  minHeight: '120px',
                  alignItems: 'flex-start',
                },
              }}
              inputProps={{
                style: {
                  overflow: 'auto',
                  overflowWrap: 'break-word',
                  whiteSpace: 'pre-wrap',
                },
              }}
            />
          </>
        )}

        {formik.values.inquiryReason === 'Media Inquiries' && (
          <>
            <TextField
              label="Deadline"
              name="deadline"
              type="date"
              value={formik.values.deadline}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
          </>
        )}

        {formik.values.inquiryReason === 'Investor Inquiries' && (
          <>
            <FormControl fullWidth>
              <InputLabel>Investment Interest Type</InputLabel>
              <Select
                name="investmentInterestType"
                value={formik.values.investmentInterestType}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <MenuItem value="Equity">Equity</MenuItem>
                <MenuItem value="Strategic Partnership">
                  Strategic Partnership
                </MenuItem>
                <MenuItem value="Funding Opportunity">
                  Funding Opportunity
                </MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
          </>
        )}

        {formik.values.inquiryReason === 'EdTech Programs' && (
          <>
            <FormControl fullWidth>
              <InputLabel>Program Interest Type</InputLabel>
              <Select
                name="programInterestType"
                value={formik.values.programInterestType}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <MenuItem value="AI for Executives">AI for Executives</MenuItem>
                <MenuItem value="AI for Analysts">AI for Analysts</MenuItem>
                <MenuItem value="Custom EdTech Training">
                  Custom EdTech Training
                </MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Desired Program Dates"
              name="desiredProgramDates"
              type="date"
              value={formik.values.desiredProgramDates}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              label="Number of Participants"
              name="numberOfParticipants"
              type="number"
              value={formik.values.numberOfParticipants}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
            />
            <TextField
              label="Additional Program Details"
              name="additionalProgramDetails"
              value={formik.values.additionalProgramDetails}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              multiline
              InputProps={{
                style: {
                  minHeight: '120px',
                  alignItems: 'flex-start',
                },
              }}
              inputProps={{
                style: {
                  overflow: 'auto',
                  overflowWrap: 'break-word',
                  whiteSpace: 'pre-wrap',
                },
              }}
            />
          </>
        )}

        {formik.values.inquiryReason === 'Data Marketplace' && (
          <>
            <FormControl fullWidth>
              <InputLabel>Marketplace Type</InputLabel>
              <Select
                name="marketplaceType"
                value={formik.values.marketplaceType}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <MenuItem value="Healthcare">Healthcare</MenuItem>
                <MenuItem value="Biosciences">Biosciences</MenuItem>
                <MenuItem value="Bioinformatics">Bioinformatics</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Data Use Case"
              name="dataUseCase"
              value={formik.values.dataUseCase}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              multiline
            />
            <TextField
              label="Specific Dataset"
              name="specificDataset"
              value={formik.values.specificDataset}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              multiline
            />
            <FormControl fullWidth>
              <InputLabel>Preferred Delivery Format</InputLabel>
              <Select
                name="preferredDeliveryFormat"
                value={formik.values.preferredDeliveryFormat}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <MenuItem value="API Integration">API Integration</MenuItem>
                <MenuItem value="File Download">File Download</MenuItem>
                <MenuItem value="Cloud Access">Cloud Access</MenuItem>
              </Select>
            </FormControl>
          </>
        )}

        {errorText && (
          <Typography className="error-text">{errorText}</Typography>
        )}

        <Button label="Submit" type="primary" btnType="submit" />
      </form>
    </Box>
  );
};

export default Form;
